import * as geom from 'jsts/org/locationtech/jts/geom';
import Util from '../utils/util';

const Coordinate = geom.Coordinate;
const GeometryFactory = geom.GeometryFactory;

const regExes = {
    typeStr: /^\s*(\w+)\s*\(\s*(.*)\s*\)\s*$/,
    emptyTypeStr: /^\s*(\w+)\s*EMPTY\s*$/,
    spaces: /\s+/,
    parenComma: /\)\s*,\s*\(/,
    doubleParenComma: /\)\s*\)\s*,\s*\(\s*\(/, // can't use {2} here
    trimParens: /^\s*\(?(.*?)\)?\s*$/
};

/**
 * Object with properties corresponding to the geometry types. Property values
 * are functions that do the actual parsing.
 * @private
 */
const parse = {
    /**
     * Convert a str to an Coordinate Object
     *
     * @param {String} wkt coordinates.
     *
     * @return {Object} Coordinate Object.
     */
    coordinate(str) {
        const array = str.trim().split(regExes.spaces);

        if (array.length === 3) {
            return new Coordinate(
                parseFloat(array[0], 10),
                parseFloat(array[1], 10),
                parseFloat(array[2], 10)
            );
        }
        return new Coordinate(
            parseFloat(array[0], 10),
            parseFloat(array[1], 10)
        );
    },

    /**
     * Convert the ordinates in an array to an array of Coordinates
     *
     * @param {String}
     *          wkt coordinates array.
     *
     * @return {Array} Array with Coordinates.
     */
    coordinates(str) {
        const coordinates = [];
        const array = str.trim().split(',');
        for (let i = 0; i < array.length; ++i) {
            coordinates.push(parse.coordinate.call(this, array[i]));
        }
        return coordinates;
    },

    /**
     * Return point geometry given a point WKT fragment.
     *
     * @param {String} str A WKT fragment representing the point.
     * @return {Point} A point geometry.
     * @private
     */
    point(str) {
        if (str === undefined) {
            return this.geometryFactory.createPoint();
        }

        return this.geometryFactory.createPoint(parse.coordinate.call(this, str));
    },

    /**
     * Return a multipoint geometry given a multipoint WKT fragment.
     *
     * @param {String} str A WKT fragment representing the multipoint.
     * @return {Point} A multipoint feature.
     * @private
     */
    multipoint(str) {
        if (str === undefined) {
            return this.geometryFactory.createMultiPoint();
        }

        let point;
        const points = str.trim().split(',');
        const components = [];
        for (let i = 0, len = points.length; i < len; ++i) {
            point = points[i].replace(regExes.trimParens, '$1');
            components.push(parse.point.call(this, point));
        }
        return this.geometryFactory.createMultiPoint(components);
    },

    /**
     * Return a linestring geometry given a linestring WKT fragment.
     *
     * @param {String} str A WKT fragment representing the linestring.
     * @return {LineString} A linestring geometry.
     * @private
     */
    linestring(str) {
        if (str === undefined) {
            return this.geometryFactory.createLineString();
        }

        const coordinates = parse.coordinates.call(this, str);
        return this.geometryFactory.createLineString(coordinates);
    },

    /**
     * Return a multilinestring geometry given a multilinestring WKT fragment.
     *
     * @param {String} str A WKT fragment representing the multilinestring.
     * @return {MultiLineString} A multilinestring geometry.
     * @private
     */
    multilinestring(str) {
        if (str === undefined) {
            return this.geometryFactory.createMultiLineString();
        }

        const array = str.trim().split(regExes.parenComma);
        const lineStrings = [];
        for (let i = 0; i < array.length; ++i) {
            const lineString = array[i].replace(regExes.trimParens, '$1');
            lineStrings.push(parse.linestring.call(this, lineString));
        }
        return this.geometryFactory.createMultiLineString(lineStrings);
    },

    /**
     * Return a polygon geometry given a polygon WKT fragment.
     *
     * @param {String} str A WKT fragment representing the polygon.
     * @return {Polygon} A polygon geometry.
     * @private
     */
    polygon(str) {
        if (str === undefined) {
            return this.geometryFactory.createPolygon();
        }

        const rings = str.trim().split(regExes.parenComma);
        let shell;
        const holes = [];
        for (let i = 0, len = rings.length; i < len; ++i) {
            const ring = rings[i].replace(regExes.trimParens, '$1');
            const coordinates = parse.coordinates.call(this, ring);
            const linearring = this.geometryFactory.createLinearRing(coordinates);
            if (i === 0) {
                shell = linearring;
            } else {
                holes.push(linearring);
            }
        }
        return this.geometryFactory.createPolygon(shell, holes);
    },

    /**
     * Return a multipolygon geometry given a multipolygon WKT fragment.
     *
     * @param {String} str A WKT fragment representing the multipolygon.
     * @return {MultiPolygon} A multipolygon geometry.
     * @private
     */
    multipolygon(str) {
        if (str === undefined) {
            return this.geometryFactory.createMultiPolygon();
        }

        let polygon;
        const polygons = str.trim().split(regExes.doubleParenComma);
        const components = [];
        for (let i = 0, len = polygons.length; i < len; ++i) {
            polygon = polygons[i].replace(regExes.trimParens, '$1');
            components.push(parse.polygon.call(this, polygon));
        }
        return this.geometryFactory.createMultiPolygon(components);
    },

    /**
     * Return a geometrycollection given a geometrycollection WKT fragment.
     *
     * @param {String} string A WKT fragment representing the geometrycollection.
     * @return {GeometryCollection}
     * @private
     */
    geometrycollection(string) {
        if (string === undefined) {
            return this.geometryFactory.createGeometryCollection();
        }

        // separate components of the collection with |
        const str = string.replace(/,\s*([A-Za-z])/g, '|$1');
        const wktArray = str.trim().split('|');
        const components = [];
        for (let i = 0, len = wktArray.length; i < len; ++i) {
            components.push(this.read(wktArray[i]));
        }
        return this.geometryFactory.createGeometryCollection(components);
    }
};

/**
 * Object with properties corresponding to the geometry types. Property values
 * are functions that do the actual data extraction.
 * @private
 */
const extract = {
    /**
     * Convert a Coordinate to an String
     *
     * @param {Coordinate}
     *          coordinate Coordinate to convert.
     *
     * @return {Array} Array of ordinates.
     */
    coordinate(coordinate) {
        if (!Util.isNil(coordinate.z)) {
            return `${coordinate.x} ${coordinate.y} ${coordinate.z}`;
        }

        return `${coordinate.x} ${coordinate.y}`;
    },

    /**
     * Return a space delimited string of point coordinates.
     *
     * @param {Point}
     *          point
     * @return {String} A string of coordinates representing the point.
     */
    point(point) {
        return extract.coordinate.call(this, point.getCoordinate());
    },

    /**
     * Return a comma delimited string of point coordinates from a multipoint.
     *
     * @param {MultiPoint}
     *          multipoint
     * @return {String} A string of point coordinate strings representing the
     *         multipoint.
     */
    multipoint(multipoint) {
        const array = [];
        const num = multipoint.getNumGeometries();
        for (let i = 0; i < num; ++i) {
            const point = multipoint.getGeometryN(i);
            array.push(`(${extract.point.call(this, point)})`);
        }
        return array.join(',');
    },

    /**
     * Return a comma delimited string of point coordinates from a line.
     *
     * @param {LineString} linestring
     * @return {String} A string of point coordinate strings representing the linestring.
     */
    linestring(linestring) {
        const array = [];
        const coordinates = linestring.getCoordinates();
        for (let i = 0; i < coordinates.length; ++i) {
            const coordinate = coordinates[i];
            array.push(extract.coordinate.call(this, coordinate));
        }
        return array.join(',');
    },

    /**
     * Return a comma delimited string of linestring strings from a
     * multilinestring.
     *
     * @param {MultiLineString} multilinestring
     * @return {String} A string of of linestring strings representing the multilinestring.
     */
    multilinestring(multilinestring) {
        const array = [];
        const num = multilinestring.getNumGeometries();
        for (let i = 0; i < num; ++i) {
            const linestring = multilinestring.getGeometryN(i);
            array.push(`(${extract.linestring.call(this, linestring)})`);
        }
        return array.join(',');
    },

    /**
     * Return a comma delimited string of linear ring arrays from a polygon.
     *
     * @param {Polygon} polygon
     * @return {String} An array of linear ring arrays representing the polygon.
     */
    polygon(polygon) {
        const array = [];
        array.push(`(${extract.linestring.call(this, polygon.getExteriorRing())})`);
        const num = polygon.getNumInteriorRing();
        for (let i = 0; i < num; ++i) {
            const hole = polygon.getInteriorRingN(i);
            array.push(`(${extract.linestring.call(this, hole)})`);
        }
        return array.join(',');
    },

    /**
     * Return an array of polygon arrays from a multipolygon.
     *
     * @param {MultiPolygon} multipolygon
     * @return {String} An array of polygon arrays representing the multipolygon.
     */
    multipolygon(multipolygon) {
        const array = [];
        const num = multipolygon.getNumGeometries();
        for (let i = 0; i < num; ++i) {
            const polygon = multipolygon.getGeometryN(i);
            array.push(`(${extract.polygon.call(this, polygon)})`);
        }
        return array.join(',');
    },

    /**
     * Return the WKT portion between 'GEOMETRYCOLLECTION(' and ')' for an
     * geometrycollection.
     *
     * @param {GeometryCollection} collection
     * @return {String} internal WKT representation of the collection.
     */
    geometrycollection(collection) {
        const array = [];
        const num = collection.getNumGeometries();
        for (let i = 0; i < num; ++i) {
            const geometry = collection.getGeometryN(i);
            array.push(this.extractGeometry(geometry));
        }
        return array.join(',');
    }
};

/**
 * Class for reading and writing Well-Known Text.
 *
 * NOTE: Adapted from OpenLayers 2.11 implementation.
 */
export default class WKTParser {
    /** Create a new parser for WKT
     *
     * @param {GeometryFactory} geometryFactory
     * @return An instance of WKTParser.
     * @constructor
     * @private
     */
    constructor(geometryFactory) {
        this.geometryFactory = geometryFactory || new GeometryFactory();
    }

    /**
     * Deserialize a WKT string and return a geometry. Supports WKT for POINT,
     * MULTIPOINT, LINESTRING, LINEARRING, MULTILINESTRING, POLYGON, MULTIPOLYGON,
     * and GEOMETRYCOLLECTION.
     *
     * @param {String} string A WKT string.
     * @return {Geometry} A geometry instance.
     * @private
     */
    read(string) {
        let geometry;
        let type;
        let str;
        const wkt = string.replace(/[\n\r]/g, ' ');
        let matches = regExes.typeStr.exec(wkt);
        if (wkt.search('EMPTY') !== -1) {
            matches = regExes.emptyTypeStr.exec(wkt);
            matches[2] = undefined;
        }
        if (matches) {
            type = matches[1].toLowerCase();
            str = matches[2];
            if (parse[type]) {
                geometry = parse[type].call(this, str);
            }
        }

        if (geometry === undefined) {
            throw new Error(`Could not parse WKT ${wkt}`);
        }

        return geometry;
    }

    /**
     * Serialize a geometry into a WKT string.
     *
     * @param {Geometry} geometry A feature or array of features.
     * @return {String} The WKT string representation of the input geometries.
     * @private
     */
    write(geometry) {
        return this.extractGeometry(geometry);
    }

    /**
     * Entry point to construct the WKT for a single Geometry object.
     *
     * @param {Geometry} geometry
     * @return {String} A WKT string of representing the geometry.
     * @private
     */
    extractGeometry(geometry) {
        const type = geometry.getGeometryType().toLowerCase();
        if (!extract[type]) {
            return null;
        }
        const wktType = type.toUpperCase();
        let data;
        if (geometry.isEmpty()) {
            data = `${wktType} EMPTY`;
        } else {
            data = `${wktType}(${extract[type].call(this, geometry)})`;
        }
        return data;
    }
}
